// LongSessionPrompt — fallback do fallback. Só dispara se cleanup ativo
// não foi suficiente.
//
// Decisão tech lead: degradar UI silenciosamente é mentira. Cleanup
// silencioso é solução real. Prompt é o ÚLTIMO recurso, e aparece
// apenas com EVIDÊNCIA OBJETIVA de problema.
//
// Trigger: long-session-cleaner emite 'suggest' (8h+). Aqui:
// 1. Se sessionStorage marcou dismiss recente (1h) → não mostra.
// 2. Health check: se heap < 85% do limit (Chromium) → não mostra
//    (cleanup deu conta).
// 3. Em browsers sem performance.memory (Safari/Tizen antigo): só
//    mostra se sessão > 12h (fallback conservador).
// 4. Se passou em todos: mostra prompt com copy Opção 1.


import { getSignals } from '@app-iptv/device'
import { useTranslation } from '@app-iptv/i18n'
import { useEffect, useRef, useState } from 'react'

import { flushQoe, onLongSessionSuggest } from '../long-session-cleaner'

import { ConfirmDialog } from './ConfirmDialog'

const DISMISS_KEY = 'iptv:long-session-dismissed-at'
const DISMISS_TTL_MS = 60 * 60 * 1000 // 1h

// Threshold heap dinâmico (M4 do code review). Tizen 2017 tem
// jsHeapSizeLimit ~100-150MB (vs Chrome desktop 2-4GB). Uso saudável
// nesses devices regularmente passa de 85% → false positive. Devices
// constrangidos exigem threshold mais alto pra evitar prompt em
// cenário normal.
const HEAP_PRESSURE_LIMITED = 0.92 // limit < 256MB (Tizen/webOS antigos)
const HEAP_PRESSURE_NORMAL = 0.85 // limit >= 256MB (Chrome, Tizen 6+)
const HEAP_LIMITED_THRESHOLD_MB = 256

const FALLBACK_HOURS = 12 // browsers sem performance.memory

interface MemoryInfo {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

function isDismissed(): boolean {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const at = Number(raw)
    if (!Number.isFinite(at)) return false
    return Date.now() - at < DISMISS_TTL_MS
  } catch {
    return false
  }
}

function markDismissed(): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch {
    // sessionStorage indisponível (modo privado): cleaner só notifica
    // 1× por transição pra 'suggest' nesta sessão de qualquer forma.
  }
}

/** Health check — decide se há evidência objetiva de problema.
 *  Retorna true SE prompt deve ser mostrado. */
function shouldShowPrompt(): boolean {
  // Chromium: usa performance.memory (não-padrão mas confiável onde
  // existe — Tizen 6+, webOS 6+, Chrome desktop).
  const mem = (performance as unknown as { memory?: MemoryInfo }).memory
  if (mem && mem.jsHeapSizeLimit > 0) {
    const limitMB = mem.jsHeapSizeLimit / 1024 / 1024
    const pressure = mem.usedJSHeapSize / mem.jsHeapSizeLimit
    // Threshold dinâmico: devices com limit pequeno (Tizen 2017 tem
    // ~100-150MB) usam saudavelmente 80%+ do limit. Forçar 0.85 ali
    // é false positive. Devices grandes (Chrome desktop ~4GB) raramente
    // chegam a 50% — 0.85 é generoso.
    const threshold =
      limitMB < HEAP_LIMITED_THRESHOLD_MB
        ? HEAP_PRESSURE_LIMITED
        : HEAP_PRESSURE_NORMAL
    if (import.meta.env.DEV) {
      console.warn(
        `[long-session-prompt] heap=${(pressure * 100).toFixed(1)}% limit=${limitMB.toFixed(0)}MB threshold=${(threshold * 100).toFixed(0)}%`,
      )
    }
    return pressure >= threshold
  }
  // Fallback: sem performance.memory (Safari, Firefox, Tizen 2017).
  // Sem dado de heap, usa só duração da sessão. cleaner já gating em 8h
  // (longSessionMode === 'suggest'); aqui exigimos 12h pra mostrar SEM
  // evidência de heap — conservador, evita prompt desnecessário em
  // sessões saudáveis.
  const sessionAgeMs = Date.now() - getSignals().sessionStartedAt
  return sessionAgeMs >= FALLBACK_HOURS * 60 * 60 * 1000
}

export function LongSessionPrompt() {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  // M5 do code review: dismiss in-memory + sessionStorage. Em modo
  // privado/Tizen sem storage, sessionStorage falha silencioso →
  // re-emit de 'suggest' poderia mostrar prompt repetidamente. Ref
  // garante 1× por sessão MESMO sem storage.
  const dismissedRef = useRef(false)

  useEffect(() => {
    return onLongSessionSuggest(() => {
      if (dismissedRef.current || isDismissed()) return
      if (!shouldShowPrompt()) {
        if (import.meta.env.DEV) {
          console.warn('[long-session-prompt] suggest disparou mas health OK')
        }
        return
      }
      setShow(true)
    })
  }, [])

  if (!show) return null

  return (
    <ConfirmDialog
      title={t('longSession.title')}
      message={t('longSession.message')}
      confirmLabel={t('longSession.confirm')}
      cancelLabel={t('longSession.dismiss')}
      onConfirm={async () => {
        // Flush QoE pendentes ANTES de reload — sem isso o evento
        // "user reloaded de prompt" se perde no buffer (m2 do review).
        await flushQoe()
        // Reload completo — joga fora heap, MSE, listeners, tudo.
        window.location.reload()
      }}
      onCancel={() => {
        dismissedRef.current = true
        markDismissed()
        setShow(false)
      }}
    />
  )
}
